import React from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, Circle, CircleDot } from 'lucide-react';

interface MohreTimelineProps {
  /** Whole days from the due date. Null marks no position: clock off, or a month whose zone is not running (wpsClockRunning). */
  daysAfterDue: number | null;
  /** The month reads marked as submitted, so the clock has stopped. */
  stopped: boolean;
}

/**
 * MR 340/2026 Annex 1, in the Annex's wording and never as calendar dates. The first three steps show the current
 * position, turned a day early by decision (Overdue starts on the 5th). The later three depend on repeat violations and
 * worker numbers that one tenant cannot see, so they stay greyed and are never computed.
 */
export const MohreTimeline: React.FC<MohreTimelineProps> = ({ daysAfterDue, stopped }) => {
  const { t } = useTranslation();

  const steps = [
    { reachedAt: 0, when: t('payroll.wps.uae.timeline.when0'), label: t('payroll.wps.uae.timeline.step0') },
    { reachedAt: 1, when: t('payroll.wps.uae.timeline.when2'), label: t('payroll.wps.uae.timeline.step2') },
    { reachedAt: 4, when: t('payroll.wps.uae.timeline.when5'), label: t('payroll.wps.uae.timeline.step5') },
    { reachedAt: null, when: t('payroll.wps.uae.timeline.when11'), label: t('payroll.wps.uae.timeline.step11') },
    { reachedAt: null, when: t('payroll.wps.uae.timeline.when16'), label: t('payroll.wps.uae.timeline.step16') },
    { reachedAt: null, when: t('payroll.wps.uae.timeline.when21'), label: t('payroll.wps.uae.timeline.step21') },
  ];

  const position = stopped || daysAfterDue === null
    ? -1
    : steps.reduce((last, step, i) => (step.reachedAt !== null && daysAfterDue >= step.reachedAt ? i : last), -1);

  return (
    <section className="rounded-lg border border-border bg-card p-4" data-testid="wps-mohre-timeline">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <h3 className="font-semibold text-sm">{t('payroll.wps.uae.timeline.title')}</h3>
        {stopped && (
          <span className="text-xs text-muted-foreground">{t('payroll.wps.uae.timeline.stopped')}</span>
        )}
      </div>
      <ol className="grid grid-cols-1 sm:grid-cols-3 xl:grid-cols-6 gap-3">
        {steps.map((step, i) => {
          const conditional = step.reachedAt === null;
          const current = i === position;
          const passed = i < position;
          let Icon = Circle;
          if (current) Icon = CircleDot;
          else if (passed) Icon = CheckCircle2;
          let iconClass = 'text-muted-foreground/60';
          if (current) iconClass = position === 0 ? 'text-amber-500' : 'text-red-600';
          else if (passed) iconClass = 'text-foreground/60';
          return (
            <li
              key={step.when}
              className={`flex items-start gap-2 min-w-0 ${conditional ? 'text-muted-foreground' : ''}`}
              data-testid="wps-timeline-step"
              data-current={current ? 'true' : undefined}
              aria-current={current ? 'step' : undefined}
            >
              <Icon className={`w-4 h-4 mt-0.5 shrink-0 ${iconClass}`} aria-hidden="true" />
              <div className="min-w-0">
                <p className={`text-xs ${current ? 'font-semibold text-foreground' : 'text-muted-foreground'}`}>{step.when}</p>
                <p className={`text-sm ${current ? 'font-semibold text-foreground' : ''}`}>{step.label}</p>
                {current && (
                  <p className="text-xs text-muted-foreground">{t('payroll.wps.uae.timeline.current')}</p>
                )}
                {conditional && (
                  <p className="text-xs">{t('payroll.wps.uae.timeline.conditional')}</p>
                )}
              </div>
            </li>
          );
        })}
      </ol>
    </section>
  );
};
